import React, { FC, useCallback } from 'react';
import classNames from 'classnames';
import ScrollToMe from 'src/common/components/ScrollToMe';
import TripStateIndicatorCalendar from 'src/common/components/TripStateIndicatorCalendar';
import decorateComponent from 'src/common/utils/decorateComponent';
import useCalendarFocus from 'src/calendar/hooks/useCalendarFocus';
import { UnavailableEvent } from 'src/calendar/types';
import EventShell from '../EventShell';
import Time from '../Time';

export type AdHocEventsProps = {
  events: UnavailableEvent[];
  openUnavailableEditor: (adHocEventId: number) => void;
  disabled: boolean;
  isSelectedDate: boolean;
};

const AdHocEvents: FC<AdHocEventsProps> = ({
  disabled,
  events,
  openUnavailableEditor,
  isSelectedDate,
}) => {
  const singleEvent = events.length === 1;
  const firstEvent = events[0];
  const otherEventsCount = events.length - 1;
  const durationCount = `${firstEvent.day_number}/${firstEvent.total_days}`;
  const getListingCopy = () => {
    if (singleEvent && firstEvent.imported) {
      return firstEvent.reason || '';
    } if (singleEvent) {
      return '';
    }
    return `+${otherEventsCount} listing${otherEventsCount === 1 ? '' : 's'}`;
  };
  const listingCopy = getListingCopy();
  const adHocEventId = firstEvent.adhoc_calendar_event_id;
  const { selectedDate, focusedAdHocEventId, setCalendarFocus, scrollToEvent } = useCalendarFocus();
  const isFocusedEvent = focusedAdHocEventId === adHocEventId;
  const isFirstOfAdHocGroup = firstEvent.day_number === 1 && firstEvent.event_date === selectedDate;
  const firstEventOfAddHocEventId = isFirstOfAdHocGroup && isFocusedEvent;
  const isSyncedEvent = !!firstEvent.imported;

  const eventState = {
    state: 'adHoc',
    role: 'adHoc',
    // External event
    badge: isSyncedEvent ? 'Synced' : 'Unavailable',
  };

  const handleClick = useCallback(() => {
    if (singleEvent && firstEvent.imported) {
      // TODO: different view
      return;
    }
    setCalendarFocus({ focusedAdHocEventId: adHocEventId });
    openUnavailableEditor(adHocEventId);
  }, [adHocEventId, firstEvent.imported, openUnavailableEditor, setCalendarFocus, singleEvent]);

  return (
    <EventShell
      className={classNames({
        focusedEvent: isFocusedEvent,
      })}
      classNameModifier={classNames({ adHocSingleEvent: singleEvent })}
      disabled={disabled || isSyncedEvent}
      eventType="adHoc"
      handleClick={handleClick}
      groupId={`adHoc-${adHocEventId}`}
      time={<Time event={firstEvent} isUnavailableEvent />}
      multiDayDuration={firstEvent.total_days > 1 ? durationCount : undefined}
      title={listingCopy}
      tripStateIndicator={<TripStateIndicatorCalendar tripState={eventState} />}
      wrapTitleToNextRow={isSyncedEvent}
    >
      {isSelectedDate && firstEventOfAddHocEventId && scrollToEvent && (
        <ScrollToMe
          key={`scroll-to-event-${selectedDate}-${firstEvent.adhoc_calendar_event_id}`}
          classNameModifier="calendarEventAnchor"
        />
      )}
    </EventShell>
  );
};

export default decorateComponent(AdHocEvents);
